import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Drawer, Button } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeOutlined,
  EditOutlined,
  CopyOutlined,
  HistoryOutlined,
  BarChartOutlined,
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { getUser } from '../utils/auth';
import './MainLayout.css';

const { Header, Content, Sider } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileMenuVisible(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // 路由变化时关闭移动端菜单
    setMobileMenuVisible(false);
  }, [location.pathname]);

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: <Link to="/">首页</Link>,
    },
    {
      key: '/essay/new',
      icon: <EditOutlined />,
      label: <Link to="/essay/new">新建批改</Link>,
    },
    {
      key: '/batch-correction',
      icon: <CopyOutlined />,
      label: <Link to="/batch-correction">批量批改</Link>,
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: <Link to="/history">批改记录</Link>,
    },
    {
      key: '/statistics',
      icon: <DashboardOutlined />,
      label: <Link to="/statistics">批改统计</Link>,
    },
    {
      key: 'management',
      icon: <TeamOutlined />,
      label: '学习档案',
      children: [
        {
          key: '/classes',
          label: <Link to="/classes">班级信息</Link>,
        },
        {
          key: '/students',
          label: <Link to="/students">同学档案</Link>,
        },
        {
          key: '/topics',
          label: <Link to="/topics">题目管理</Link>,
        },
      ],
    },
    {
      key: '/analysis',
      icon: <BarChartOutlined />,
      label: <Link to="/analysis">成长曲线</Link>,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'logout') {
      onLogout();
    }
  };

  const userMenu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        个人资料
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />}>
        退出登录
      </Menu.Item>
    </Menu>
  );

  const getMobileNavKey = () => {
    if (location.pathname === '/') {
      return '/';
    }
    if (location.pathname.startsWith('/essay') || location.pathname.startsWith('/batch-correction')) {
      return '/essay/new';
    }
    if (location.pathname.startsWith('/history')) {
      return '/history';
    }
    if (location.pathname.startsWith('/profile')) {
      return '/profile';
    }
    return location.pathname;
  };

  const mobileNavKey = getMobileNavKey();

  // 移动端底部导航只保留一级高频入口，二级页面通过当前内容区完成操作。
  const MobileBottomNav = () => (
    <div className="mobile-bottom-nav">
      <Link to="/" className={`mobile-bottom-nav-item ${mobileNavKey === '/' ? 'active' : ''}`}>
        <HomeOutlined />
        <span>首页</span>
      </Link>
      <Link to="/essay/new" className={`mobile-bottom-nav-item ${mobileNavKey === '/essay/new' ? 'active' : ''}`}>
        <EditOutlined />
        <span>批改</span>
      </Link>
      <Link to="/history" className={`mobile-bottom-nav-item ${mobileNavKey === '/history' ? 'active' : ''}`}>
        <HistoryOutlined />
        <span>历史</span>
      </Link>
      <Link to="/profile" className={`mobile-bottom-nav-item ${mobileNavKey === '/profile' ? 'active' : ''}`}>
        <UserOutlined />
        <span>我的</span>
      </Link>
    </div>
  );

  return (
    <Layout className={`main-layout ${isMobile ? 'main-layout-mobile' : ''}`}>
      {!isMobile ? (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          className="layout-sider"
        >
          <div className="logo">
            {collapsed ? '批改' : '作文批改系统'}
          </div>
          <Menu
            theme="light"
            selectedKeys={[location.pathname]}
            mode="inline"
            items={menuItems}
          />
        </Sider>
      ) : (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          bodyStyle={{ padding: 0 }}
          width={250}
        >
          <div className="logo mobile-drawer-logo">
            作文批改系统
          </div>
          <Menu
            theme="light"
            selectedKeys={[location.pathname]}
            mode="inline"
            items={menuItems}
            style={{ height: 'calc(100vh - 64px)' }}
          />
        </Drawer>
      )}
      <Layout>
        <Header className="layout-header">
          <div className="header-content">
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileMenuVisible(true)}
                style={{ marginRight: 16 }}
              />
            )}
            <div className="header-title">
              {isMobile ? '英语批改' : '英语作文批改系统'}
            </div>
            <Dropdown overlay={userMenu} placement="bottomRight" trigger={['click']}>
              <Space className="user-info" onClick={e => e.preventDefault()}>
                <Avatar icon={<UserOutlined />} />
                {!isMobile && <span>{user?.name || '用户'}</span>}
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content className="layout-content">
          {children}
        </Content>
        {isMobile && <MobileBottomNav />}
      </Layout>
    </Layout>
  );
};

export default MainLayout;
